/**
 * SecurityOverview — Super Admin dashboard tab that surfaces credential-export
 * activity, suspicious flags, top schools and a daily timeline.
 */
import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { ShieldAlert, RefreshCw, Activity, Building2, User, AlertTriangle, KeyRound, Loader2, Mail } from "lucide-react";

export default function SecurityOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [windowDays, setWindowDays] = useState(30);

  const fetchData = useCallback(async (days = windowDays) => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/credentials/security-overview?timeline_days=${days}`,
        { withCredentials: true }
      );
      setData(data);
    } catch (e) {
      console.error("Failed to load security overview:", e);
      toast.error("No se pudo cargar el panel de seguridad");
    } finally {
      setLoading(false);
    }
  }, [windowDays]);

  useEffect(() => { fetchData(windowDays); }, [fetchData, windowDays]);

  const maxExport = useMemo(() => {
    if (!data?.timeline) return 1;
    return Math.max(1, ...data.timeline.map((d) => d.exports));
  }, [data]);

  if (!data && loading) {
    return (
      <div className="p-10 text-center text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" />
        Cargando panel de seguridad…
      </div>
    );
  }
  if (!data) return null;

  const flagSeverity = {
    warn: "bg-amber-50 border-amber-200 text-amber-800",
    info: "bg-blue-50 border-blue-200 text-blue-800",
    danger: "bg-red-50 border-red-200 text-red-800",
  };

  return (
    <div className="space-y-6" data-testid="security-overview">
      {/* Header + controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-amber-500 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Seguridad · Auditoría de credenciales</h2>
            <p className="text-xs text-gray-500">
              Generado {data.generated_at ? new Date(data.generated_at).toLocaleString("es-ES") : "—"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={windowDays}
            onChange={(e) => { const v = Number(e.target.value); setWindowDays(v); fetchData(v); }}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white"
            data-testid="sec-window-select"
          >
            <option value={7}>Últimos 7 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={60}>Últimos 60 días</option>
            <option value={90}>Últimos 90 días</option>
          </select>
          <button
            onClick={() => fetchData(windowDays)}
            disabled={loading}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 flex items-center gap-1.5"
            data-testid="sec-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refrescar
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Exports últimos 7 días" value={data.windows.last_7d} color="from-blue-500 to-cyan-500" icon={Activity} testid="kpi-7d" />
        <Kpi label="Exports últimos 30 días" value={data.windows.last_30d} color="from-indigo-500 to-violet-500" icon={Activity} testid="kpi-30d" />
        <Kpi label="Exports últimos 90 días" value={data.windows.last_90d} color="from-fuchsia-500 to-pink-500" icon={Activity} testid="kpi-90d" />
        <Kpi
          label="Resets de contraseña (30d)"
          value={`${data.resets_30d.events} · ${data.resets_30d.users_affected} usr.`}
          color="from-amber-500 to-orange-500"
          icon={KeyRound}
          testid="kpi-resets"
        />
      </div>

      {/* Flags */}
      <section className="bg-white rounded-2xl border border-gray-100 p-5">
        <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500" /> Banderas (últimos 7 días)
        </h3>
        {data.flags.length === 0 ? (
          <p className="text-sm text-gray-400" data-testid="flags-empty">
            Sin actividad sospechosa — todo limpio.
          </p>
        ) : (
          <ul className="space-y-2" data-testid="flags-list">
            {data.flags.map((f, i) => (
              <li
                key={`${f.kind}-${i}`}
                className={`px-3 py-2.5 rounded-lg border text-sm ${flagSeverity[f.severity] || flagSeverity.info}`}
                data-testid={`flag-${f.kind}-${i}`}
              >
                <span className="font-semibold mr-1 uppercase text-[10px] tracking-wide">{f.kind}</span>
                {f.message}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Email alerts sent */}
      <section className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="alerts-sent">
        <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
          <Mail className="w-4 h-4 text-rose-500" /> Alertas enviadas por email (últimos 7 días)
        </h3>
        {!data.alerts_sent_7d || data.alerts_sent_7d.length === 0 ? (
          <p className="text-sm text-gray-400">
            No se han enviado alertas todavía.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {data.alerts_sent_7d.map((a) => (
              <li
                key={a.key}
                className="flex items-center justify-between text-xs px-3 py-2 rounded-lg bg-rose-50/60 border border-rose-100"
                data-testid={`alert-sent-${a.kind}`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="font-mono uppercase text-[10px] tracking-wide text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded">
                    {a.kind}
                  </span>
                  <span className="text-gray-700 truncate">
                    {a.kind === "mass_reset"
                      ? `${a.payload?.users_exported ?? "?"} usuarios reseteados`
                      : a.kind === "ip_spike"
                        ? `IP ${a.payload?.ip} · ${a.payload?.count ?? "?"} exports en ${a.payload?.day}`
                        : a.key}
                  </span>
                </span>
                <span className="text-gray-400 whitespace-nowrap ml-2">
                  {a.created_at ? new Date(a.created_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : "—"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Timeline */}
      <section className="bg-white rounded-2xl border border-gray-100 p-5">
        <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4 text-violet-500" />
          Actividad diaria (últimos {data.timeline_window_days} días)
        </h3>
        <div className="flex items-end gap-1 h-32" data-testid="timeline-bars">
          {data.timeline.map((d) => {
            const h = (d.exports / maxExport) * 100;
            return (
              <div key={d.date} className="flex-1 min-w-0 flex flex-col items-center group">
                <div className="w-full flex flex-col items-center justify-end h-full">
                  <div
                    className="w-full bg-gradient-to-t from-[#419cca] to-[#8b5cf6] rounded-t hover:opacity-80 transition-opacity"
                    style={{ height: `${h}%`, minHeight: d.exports > 0 ? "4px" : "0" }}
                    title={`${d.date}: ${d.exports} exports · ${d.users} usuarios`}
                  />
                </div>
                <span className="text-[9px] text-gray-400 mt-1 whitespace-nowrap">
                  {d.date.slice(5)}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {/* Top schools + Top users */}
      <div className="grid md:grid-cols-2 gap-4">
        <section className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="top-schools">
          <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-blue-500" /> Top colegios (30 días)
          </h3>
          {data.top_schools_30d.length === 0 ? (
            <p className="text-xs text-gray-400">Sin datos.</p>
          ) : (
            <ul className="space-y-2">
              {data.top_schools_30d.map((s, i) => (
                <li key={s.school_id} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-xs text-gray-400 w-5">{i + 1}.</span>
                    <span className="truncate text-gray-800">{s.school_name}</span>
                  </span>
                  <span className="flex items-center gap-3 text-xs text-gray-500 whitespace-nowrap">
                    <span title="Exports"><b className="text-gray-900">{s.exports}</b> exp</span>
                    <span title="Usuarios"><b className="text-gray-900">{s.users}</b> usr</span>
                    {s.resets > 0 && (
                      <span className="text-amber-700" title="Resets">
                        <KeyRound className="w-3 h-3 inline" /> {s.resets}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="top-users">
          <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
            <User className="w-4 h-4 text-fuchsia-500" /> Top usuarios (30 días)
          </h3>
          {data.top_users_30d.length === 0 ? (
            <p className="text-xs text-gray-400">Sin datos.</p>
          ) : (
            <ul className="space-y-2">
              {data.top_users_30d.map((u, i) => (
                <li key={u.user_id} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-xs text-gray-400 w-5">{i + 1}.</span>
                    <span className="truncate">
                      <span className="text-gray-800">{u.name}</span>
                      <span className="text-[10px] text-gray-400 ml-1">{u.role}</span>
                    </span>
                  </span>
                  <span className="flex items-center gap-3 text-xs text-gray-500 whitespace-nowrap">
                    <span><b className="text-gray-900">{u.exports}</b> exp</span>
                    <span><b className="text-gray-900">{u.users}</b> usr</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function Kpi({ label, value, color, icon: Icon, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
    </div>
  );
}
